import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { ArrowLeft, Save, Clock, User, CheckCircle, AlertTriangle, Cpu } from 'lucide-react';

const STATUS_ORDER = ['Open', 'In Progress', 'Pending Close', 'Closed'];

export default function WorkOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [wo, setWo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [statusChange, setStatusChange] = useState('');
  const [aiAction, setAiAction] = useState('');

  useEffect(() => { loadWO(); }, [id]);

  const loadWO = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/work-orders/${id}`);
      setWo(res.data);
      setForm(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/work-orders/${id}`, form);
      setEditMode(false);
      loadWO();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async () => {
    if (!statusChange) return;
    try {
      await api.post(`/work-orders/${id}/status`, { status: statusChange });
      setStatusChange('');
      loadWO();
    } catch (e) { alert(e.response?.data?.error || 'Status change failed'); }
  };

  const handleAIAction = async (action) => {
    try {
      await api.post(`/work-orders/${id}/ai-action`, { action });
      loadWO();
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="loading-screen"><div className="spinner"></div></div>;
  if (!wo) return <div className="empty-state"><h4>Work Order not found</h4></div>;

  const canEdit = wo.status !== 'Closed';
  const canChangeStatus = (nextStatus) => {
    const idx = STATUS_ORDER.indexOf(wo.status);
    const nextIdx = STATUS_ORDER.indexOf(nextStatus);
    return nextIdx === idx + 1;
  };

  return (
    <div>
      <div className="flex-between mb-16">
        <button className="btn btn-outline" onClick={() => navigate('/work-orders')}><ArrowLeft size={15} /> Back to Work Orders</button>
        <div style={{ display: 'flex', gap: 8 }}>
          {wo.source === 'AI' && wo.ai_status === 'Pending' && (
            <>
              <button className="btn btn-success" onClick={() => handleAIAction('Accepted')}><CheckCircle size={15} /> Accept AI Job</button>
              <button className="btn btn-outline" onClick={() => handleAIAction('Rejected')}><AlertTriangle size={15} /> Reject</button>
            </>
          )}
          {canEdit && <button className="btn btn-primary" onClick={() => setEditMode(!editMode)}>{editMode ? 'Cancel' : 'Edit'}</button>}
        </div>
      </div>

      {/* Header */}
      <div className="card mb-16">
        <div className="card-body">
          <div className="flex-between">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <h2 style={{ fontSize: 20, fontWeight: 700 }}>{wo.wo_number}</h2>
                <span className={`badge badge-${wo.status.toLowerCase().replace(' ', '')}`} style={{ fontSize: 13 }}>{wo.status}</span>
                {wo.source === 'AI' && <span className="badge badge-ai">AI Generated</span>}
                <span className={`badge badge-${wo.priority.toLowerCase()}`}>{wo.priority} Priority</span>
              </div>
              <h3 style={{ fontSize: 16, fontWeight: 500, color: 'var(--text-secondary)' }}>{wo.job_title}</h3>
            </div>
            <div style={{ textAlign: 'right' }}>
              {wo.due_date && <div style={{ fontSize: 13, color: new Date(wo.due_date) < new Date() && !['Closed'].includes(wo.status) ? 'var(--danger)' : 'var(--text)' }}>
                Due: <strong>{wo.due_date}</strong>
                {new Date(wo.due_date) < new Date() && !['Closed'].includes(wo.status) && <span style={{ color: 'var(--danger)', fontSize: 11, display: 'block' }}>OVERDUE</span>}
              </div>}
            </div>
          </div>

          {/* AI Box */}
          {wo.ai_reason && (
            <div className="ai-box">
              <div className="ai-box-header"><Cpu size={14} /><span>AI Analysis</span></div>
              <p><strong>Confidence:</strong> {wo.ai_confidence}% | <strong>Health Score:</strong> {wo.ai_health_score} | <strong>Status:</strong> {wo.ai_status}</p>
              <p>{wo.ai_reason}</p>
              {wo.ai_rejection_reason && <p style={{ color: '#dc2626', marginTop: 4 }}><strong>Rejection Reason:</strong> {wo.ai_rejection_reason}</p>}
            </div>
          )}

          {/* Status workflow */}
          {canEdit && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 10 }}>CHANGE STATUS</p>
              <div style={{ display: 'flex', gap: 6 }}>
                {STATUS_ORDER.map(s => {
                  if (s === 'Closed' && wo.status !== 'Pending Close') return null;
                  if (s === 'Pending Close' && wo.status !== 'In Progress' && wo.status !== 'Deferred') return null;
                  return (
                    <button
                      key={s}
                      className={`btn btn-sm ${statusChange === s ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => setStatusChange(s === wo.status ? '' : s)}
                      disabled={s === wo.status}
                    >
                      {s}
                    </button>
                  );
                })}
                {statusChange && (
                  <button className="btn btn-accent btn-sm" onClick={handleStatusChange}>
                    <CheckCircle size={14} /> Confirm: {statusChange}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="detail-grid">
        {/* Left Column */}
        <div>
          <div className="detail-section">
            <div className="detail-section-header">Work Order Details</div>
            <div className="detail-section-body">
              {[
                { label: 'WO Number', value: wo.wo_number },
                { label: 'Source', value: wo.source },
                { label: 'Type', value: wo.wo_type || wo.source_type || wo.source },
                { label: 'Department', value: wo.department },
                { label: 'Equipment', value: wo.component_name || '—' },
                { label: 'Raised By', value: wo.raised_by_name || wo.raised_by },
                { label: 'Raised Date', value: wo.created_at?.split('T')[0] },
                { label: 'Assigned To', value: wo.assigned_to_name || wo.assigned_to },
                { label: 'Planned Start', value: wo.planned_start_date || '—' },
                { label: 'Due Date', value: wo.due_date || '—' },
                { label: 'Running Hours Due', value: wo.running_hours_due || '—' },
              ].map(row => (
                <div key={row.label} className="detail-row">
                  <div className="detail-label">{row.label}</div>
                  <div className="detail-value" style={{ fontWeight: row.value ? 500 : 400 }}>{row.value || '—'}</div>
                </div>
              ))}
            </div>
          </div>

          {wo.problem_description && (
            <div className="detail-section">
              <div className="detail-section-header">Problem Description</div>
              <div className="detail-section-body">
                {editMode ? (
                  <textarea className="form-textarea" value={form.problem_description || ''} onChange={e => setForm({ ...form, problem_description: e.target.value })} />
                ) : (
                  <p style={{ fontSize: 13 }}>{wo.problem_description}</p>
                )}
              </div>
            </div>
          )}

          {canEdit && (
            <div className="detail-section">
              <div className="detail-section-header">Completion Details</div>
              <div className="detail-section-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Actual Hours Taken</label>
                    {editMode ? <input className="form-input" type="number" value={form.actual_hours_taken || ''} onChange={e => setForm({ ...form, actual_hours_taken: parseFloat(e.target.value) })} /> : <p>{wo.actual_hours_taken || '—'}</p>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Completed Hours (Counter)</label>
                    {editMode ? <input className="form-input" type="number" value={form.completed_hours || ''} onChange={e => setForm({ ...form, completed_hours: parseFloat(e.target.value) })} /> : <p>{wo.completed_hours || '—'}</p>}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Completion Date</label>
                  {editMode ? <input className="form-input" type="date" value={form.completion_date || ''} onChange={e => setForm({ ...form, completion_date: e.target.value })} /> : <p>{wo.completion_date || '—'}</p>}
                </div>
                <div className="form-group">
                  <label className="form-label">Job Notes</label>
                  {editMode ? <textarea className="form-textarea" value={form.job_notes || ''} onChange={e => setForm({ ...form, job_notes: e.target.value })} /> : <p style={{ fontSize: 13 }}>{wo.job_notes || '—'}</p>}
                </div>
                {wo.status === 'Closed' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Root Cause</label>
                      <p style={{ fontSize: 13 }}>{wo.root_cause || '—'}</p>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Corrective Action</label>
                      <p style={{ fontSize: 13 }}>{wo.corrective_action || '—'}</p>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Preventive Action</label>
                      <p style={{ fontSize: 13 }}>{wo.preventive_action || '—'}</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {editMode && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-outline" onClick={() => { setEditMode(false); setForm(wo); }}>Cancel</button>
              <button className="btn btn-accent" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div>
          <div className="detail-section">
            <div className="detail-section-header">Equipment Details</div>
            <div className="detail-section-body">
              {[
                { label: 'Name', value: wo.component_name },
                { label: 'Maker', value: wo.maker },
                { label: 'Model', value: wo.model_type },
                { label: 'Serial #', value: wo.serial_number },
                { label: 'Criticality', value: wo.criticality },
              ].map(r => (
                <div key={r.label} className="detail-row">
                  <div className="detail-label">{r.label}</div>
                  <div className="detail-value">{r.value || '—'}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="detail-section">
            <div className="detail-section-header">Parts Used</div>
            <div className="detail-section-body">
              {wo.partsUsedDetails && wo.partsUsedDetails.length > 0 ? (
                <table style={{ width: '100%' }}>
                  <thead><tr><th>Part</th><th>Qty</th></tr></thead>
                  <tbody>
                    {wo.partsUsedDetails.map(p => (
                      <tr key={p.partId}>
                        <td>{p.part?.part_name || p.partId}</td>
                        <td>{p.quantity} {p.part?.unit_of_measure}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : <p className="text-secondary text-sm">No parts recorded</p>}
            </div>
          </div>

          {wo.status === 'Closed' && (
            <div className="detail-section">
              <div className="detail-section-header">Sign-Off</div>
              <div className="detail-section-body">
                <div className="detail-row">
                  <div className="detail-label">Signed Off By</div>
                  <div className="detail-value">{wo.signed_off_by_name || wo.signed_off_by || '—'}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">Closed Date</div>
                  <div className="detail-value">{wo.closed_at?.split('T')[0]}</div>
                </div>
              </div>
            </div>
          )}

          {wo.status === 'Deferred' && (
            <div className="detail-section">
              <div className="detail-section-header">Deferral Details</div>
              <div className="detail-section-body">
                <div className="detail-row">
                  <div className="detail-label">Reason</div>
                  <div className="detail-value">{wo.deferred_reason || '—'}</div>
                </div>
                <div className="detail-row">
                  <div className="detail-label">New Due Date</div>
                  <div className="detail-value">{wo.deferred_new_date || '—'}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
